import React, { useState, useEffect } from 'react';
import { Auth, API } from 'aws-amplify';
import {
  handleMappingChange,
  addNewRow,
  deleteRow,
  MappingsSection,
  handleTextTransformationChange,
  UppercaseLowercaseSection,
  SubstringExtractionSection,
  handleIgnoreCharacterChange,
  addIgnoreCharacter,
  removeIgnoreCharacter,
  IgnoreCharactersSection,
  handleIgnoreRowChange,
  addIgnoreRow,
  removeIgnoreRow,
  IgnoreRowsSection
} from './textTransformations';
import {
  handleNumberTransformationChange,
  ScalingSection,
  OffsetAdjustmentSection,
  NumberThresholdsSection,
  RoundingSection
} from './numberTransformations';
import {
  handleDateTransformationChange,
  FormatConversionSection,
  DateArithmeticSection,
  ExtractDateComponentSection,
  DateThresholdsSection
} from './dateTransformations';
import { AggregationMethodSection, handleAggregationChange } from './AggregationSection';

import { handleDelimiterChange, DelimiterSection, handleMultiMappingChange, addNewMultiRow, deleteMultiRow, MultiMappingsSection} from './multiTransformations';



//import './TransformationsTab.css';

function TransformationsTab({ configData}) {


    const [fieldsConfig, setFieldsConfig] = useState({});
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [selectedField, setSelectedField] = useState(null);
    


    const handleFieldSelect = (e) => {
        setSelectedField(e.target.value);
    };

    function processMultiColumns(configData, side) {
        // Identify columns with the data type 'multi'
        const multiColumns = configData.columns.filter(column => column.dataType === 'multi');
    
        // Prepare transformations
        const multiTransformData = multiColumns.map(column => {
            const sideFields = side === 'sideA' ? column.nameInFile1 : column.nameInFile2;
            // Pass only if there is more than one field
            if (Array.isArray(sideFields) && sideFields.length > 1) {
                return {
                    standardName: column.standardName,
                    fields: sideFields,
                    side: side,
                };
            }
            return null;
        }).filter(Boolean); // Remove null entries
    
        return multiTransformData;
    }
    
    
    
    const toggleSectionVisibility = (fieldName, section) => {
        setFieldsConfig(prevFieldsConfig => {
            const updatedConfig = {
                ...prevFieldsConfig,
                [fieldName]: {
                    ...prevFieldsConfig[fieldName],
                    [section]: !prevFieldsConfig[fieldName][section],
                },
            };
    
            return updatedConfig;
        });
    };
    



    useEffect(() => {
        if (configData) {
            // Prepare multi-transformations for sideA and sideB
            const sideAFields = processMultiColumns(configData, 'sideA');
            const sideBFields = processMultiColumns(configData, 'sideB');
    
            console.log("SideA Multi Transformations:", sideAFields);
            console.log("SideB Multi Transformations:", sideBFields);
    
            // Extend initialFieldsConfig to handle subfields in multiTransformations
            const initialFieldsConfig = configData.columns.reduce((acc, column) => {
                const sideA = Array.isArray(column.nameInFile1) ? column.nameInFile1 : [];
                const sideB = Array.isArray(column.nameInFile2) ? column.nameInFile2 : [];
    
                acc[column.standardName] = {
                    mappings: configData.mappings[column.standardName] || [],
                    ignoreCharacters: configData.ignoreCharacters[column.standardName] || [],
                    ignoreRows: configData.ignoreRows?.[column.standardName] || [],
                    textTransformations: configData.textTransformations?.[column.standardName] || {
                        caseConversion: '', // Single entry for case conversion
                        SideA: {
                            substringStart: '',
                            substringEnd: '',
                            beforeChar: '',
                            afterChar: '',
                        },
                        SideB: {
                            substringStart: '',
                            substringEnd: '',
                            beforeChar: '',
                            afterChar: '',
                        },
                    },
                    numberTransformations: configData.numberTransformations?.[column.standardName] || {
                        file1: { scalingFactor: '', offsetValue: '' },
                        file2: { scalingFactor: '', offsetValue: '' },
                        rounding: configData.rounding?.[column.standardName] || '',
                    },
                    dateTransformations: configData.dateTransformations?.[column.standardName] || {
                        format: '',
                        arithmetic: { value: '', unit: '' },
                        extract: '',
                        threshold: configData.dateThresholds?.[column.standardName]?.days || '',
                    },
                    aggregation: configData.aggregation?.[column.standardName] || {
                        method: '',
                        aggregateBy: [],
                    },
                    multiTransformations: {
                        delimiter: configData.multiTransformations?.[column.standardName]?.delimiter || '',
                        sideA: sideA.reduce((fieldsAcc, field) => {
                            fieldsAcc[field] = {
                                mappings:
                                    configData.multiTransformations?.[column.standardName]?.sideA?.[field]?.mappings || [],
                            }; // Initialize subfield-specific mappings for SideA
                            return fieldsAcc;
                        }, {}),
                        sideB: sideB.reduce((fieldsAcc, field) => {
                            fieldsAcc[field] = {
                                mappings:
                                    configData.multiTransformations?.[column.standardName]?.sideB?.[field]?.mappings || [],
                            }; // Initialize subfield-specific mappings for SideB
                            return fieldsAcc;
                        }, {}),
                    },
                    dataType: column.dataType || 'Unknown',
                    showMappings: false,
                    showIgnoreCharacters: false,
                    showNumberThresholds: false,
                    showDateThresholds: false,
                    showRounding: false,
                    showCaseConversion: false,
                    showSubstringExtraction: false,
                    showScaling: false,
                    showOffsetAdjustment: false,
                    showFormatConversion: false,
                    showDateArithmetic: false,
                    showExtractDateComponent: false,
                    showAggregation: false,
                    showDelimiter: false,
                };
    
                return acc;
            }, {});
    
            setFieldsConfig(initialFieldsConfig);
        }
    }, [configData]);
    
    

    const saveMappings = async (category) => {
        try {
            const currentUser = await Auth.currentAuthenticatedUser();
            const title = configData.title;
            const userOrganization = currentUser.attributes['email'].split('@')[1].split('.')[0];
            const apiName = 'EditMap';
            const path = '/editmap';

            const currentMappings = fieldsConfig[category]?.mappings || [];
            const currentIgnoreCharacters = fieldsConfig[category]?.ignoreCharacters || [];
            const currentIgnoreRows = fieldsConfig[category]?.ignoreRows || [];
            const currentTextTransformations = fieldsConfig[category]?.textTransformations || {
                caseConversion: '',
                SideA: { substringStart: '', substringEnd: '', beforeChar: '', afterChar: '' },
                SideB: { substringStart: '', substringEnd: '', beforeChar: '', afterChar: '' },
            };
            const currentNumberTransformations = fieldsConfig[category]?.numberTransformations || {
                file1: { scalingFactor: '', offsetValue: '' },
                file2: { scalingFactor: '', offsetValue: '' },
                rounding: ''
            };
            const currentDateTransformations = fieldsConfig[category]?.dateTransformations || {
                format: '',
                arithmetic: { value: '', unit: '' },
                extract: '',
                threshold: ''
            };
            const currentAggregation = fieldsConfig[category]?.aggregation || {
                method: '',
                aggregateBy: []
            };
            const currentMultiTransformations = fieldsConfig[category]?.multiTransformations || {
                delimiter: '',
                sideA: fieldsConfig[category]?.multiTransformations?.sideA || {},
                sideB: fieldsConfig[category]?.multiTransformations?.sideB || {},
            };

            const bodyData = {
                title: title,
                category: category,
                user: currentUser.attributes['email'],
                mappings: currentMappings,
                ignoreCharacters: currentIgnoreCharacters,
                ignoreRows: currentIgnoreRows,
                textTransformations: currentTextTransformations,
                numberTransformations: currentNumberTransformations,
                dateTransformations: currentDateTransformations,
                aggregation: currentAggregation,
                multiTransformations: currentMultiTransformations,
                organization: userOrganization,
            };

            const response = await API.post(apiName, path, { body: bodyData });

            if (response.success) {
                alert('Configuration updated successfully');
            } else {
                alert('Error saving changes, please try again.');
            }
        } catch (error) {
            alert('Error saving changes, please try again.');
        }
    };




    return (
        <div className="config-tab-container">
            {configData && (
                <div className="config-data">
                    <h2 className="transform-data-title">Select a Field to Transform</h2>
           

                  

<div className="config-accordion">
<div className="accordion-title">

     
        <div className="custom-dropdown">
            <select id="field-select" onChange={handleFieldSelect} value={selectedField || ''}>
                <option value="" >Select a Field</option>
                {configData.columns.map((column, index) => (
                    <option key={index} value={column.standardName}>
                        {column.standardName}
                    </option>
                ))}
            </select>
        </div>
    </div>
    {selectedField && (
        <div className="accordion-content">
            <div className="config-column-item">
                <h4 className="column-item-title">
                    {selectedField}
                    <span className="column-item-data-type">
                        ({configData.columns.find(col => col.standardName === selectedField).dataType || 'Unknown'})
                    </span>
                </h4>
                {/* Conditionally render buttons based on data type */}
                {configData.columns.find(col => col.standardName === selectedField).dataType === 'text' && (
                    <>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showMappings')}>
                            Mappings{fieldsConfig[selectedField]?.mappings?.length > 0 ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showIgnoreCharacters')}>
                            Ignore Characters{fieldsConfig[selectedField]?.ignoreCharacters?.length > 0 ? ' ✓' : ''}
                        </button>

                        <button onClick={() => toggleSectionVisibility(selectedField, 'showIgnoreRows')}>
                          Ignore Rows{fieldsConfig[selectedField]?.ignoreRows?.length > 0 ? ' ✓' : ''}
                        </button>

                        <button onClick={() => toggleSectionVisibility(selectedField, 'showCaseConversion')}>
                            Case Conversion{fieldsConfig[selectedField]?.textTransformations?.caseConversion ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showSubstringExtraction')}>
                        Substring Extraction
                        {fieldsConfig[selectedField]?.textTransformations?.SideA?.substringStart ||
                        fieldsConfig[selectedField]?.textTransformations?.SideA?.substringEnd ||
                        fieldsConfig[selectedField]?.textTransformations?.SideA?.beforeChar ||
                        fieldsConfig[selectedField]?.textTransformations?.SideA?.afterChar ||
                        fieldsConfig[selectedField]?.textTransformations?.SideB?.substringStart ||
                        fieldsConfig[selectedField]?.textTransformations?.SideB?.substringEnd ||
                        fieldsConfig[selectedField]?.textTransformations?.SideB?.beforeChar ||
                        fieldsConfig[selectedField]?.textTransformations?.SideB?.afterChar
                            ? ' ✓'
                            : ''}
                    </button>

                    </>
                )}
                {configData.columns.find(col => col.standardName === selectedField).dataType === 'number' && (
                    <>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showNumberThresholds')}>
                            Number Threshold
                            {fieldsConfig[selectedField]?.numberTransformations?.thresholds?.amount ||
                            fieldsConfig[selectedField]?.numberTransformations?.thresholds?.percent ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showRounding')}>
                            Round{fieldsConfig[selectedField]?.numberTransformations?.rounding ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showScaling')}>
                            Scaling
                            {fieldsConfig[selectedField]?.numberTransformations?.file1?.scalingFactor ||
                            fieldsConfig[selectedField]?.numberTransformations?.file2?.scalingFactor ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showOffsetAdjustment')}>
                            Offset Adjustment
                            {fieldsConfig[selectedField]?.numberTransformations?.file1?.offsetValue ||
                            fieldsConfig[selectedField]?.numberTransformations?.file2?.offsetValue ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showAggregation')}>
                            Aggregation
                            {fieldsConfig[selectedField]?.aggregation?.method ? ' ✓' : ''}
                        </button>
                    </>
                )}
                {configData.columns.find(col => col.standardName === selectedField).dataType === 'date' && (
                    <>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showDateThresholds')}>
                            Days Threshold{fieldsConfig[selectedField]?.dateTransformations?.threshold ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showFormatConversion')}>
                            Date Format{fieldsConfig[selectedField]?.dateTransformations?.format ? ' ✓' : ''}
                        </button>
                        <button onClick={() => toggleSectionVisibility(selectedField, 'showDateArithmetic')}>
                            Offset Days
                            {fieldsConfig[selectedField]?.dateTransformations?.arithmetic?.value ||
                            fieldsConfig[selectedField]?.dateTransformations?.arithmetic?.unit ? ' ✓' : ''}
                        </button>
                    </>
                )}

{configData.columns.find(col => col.standardName === selectedField).dataType === 'multi' && (
    <>
        <button onClick={() => toggleSectionVisibility(selectedField, 'showDelimiter')}>
            Delimiter
            {fieldsConfig[selectedField]?.multiTransformations?.delimiter ? ' ✓' : ''}
        </button>

        {/* Delimiter Section */}
        {fieldsConfig[selectedField]?.showDelimiter && (
            <DelimiterSection
                fieldName={selectedField}
                fieldsConfig={fieldsConfig}
                handleDelimiterChange={(fieldName, side, value) =>
                    handleDelimiterChange(fieldName, side, value, setFieldsConfig)
                }
            />
        )}







{/* Single Mappings Button */}
<button
  onClick={() => toggleSectionVisibility(selectedField, 'showUnifiedMappings')}
>
  Mappings
  {Object.keys(fieldsConfig[selectedField]?.multiTransformations?.sideA || {}).some(
    (subField) =>
      fieldsConfig[selectedField]?.multiTransformations?.sideA[subField]?.mappings
        ?.length > 0
  ) ||
  Object.keys(fieldsConfig[selectedField]?.multiTransformations?.sideB || {}).some(
    (subField) =>
      fieldsConfig[selectedField]?.multiTransformations?.sideB[subField]?.mappings
        ?.length > 0
  )
    ? ' ✓'
    : ''}
</button>

{/* Unified Mappings Section */}
{fieldsConfig[selectedField]?.showUnifiedMappings && (
  <div className="unified-mappings-section">
    {(() => {
      const firstSubField = Object.keys(fieldsConfig[selectedField]?.multiTransformations?.sideA || {})[0] ||
        Object.keys(fieldsConfig[selectedField]?.multiTransformations?.sideB || {})[0];

      return firstSubField ? (
        <div key={firstSubField}>
          <h6>{firstSubField}</h6>
          <MultiMappingsSection
            fieldName={firstSubField}
            fieldsConfig={fieldsConfig}
            handleMultiMappingChange={handleMultiMappingChange}
            addNewMultiRow={addNewMultiRow}
            deleteMultiRow={deleteMultiRow}
            parentField={selectedField}
            side="sideA" // Pass "sideA" or "sideB"; it doesn't affect functionality as it's unified
            setFieldsConfig={setFieldsConfig}
          />
        </div>
      ) : null;
    })()}
  </div>
)}
    </>
)}







                {/* Mappings section */}
                {fieldsConfig[selectedField]?.showMappings && (
                    <MappingsSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleMappingChange={(fieldName, mapIndex, type, newValue) =>
                            handleMappingChange(fieldName, mapIndex, type, newValue, setFieldsConfig)
                        }
                        addNewRow={(fieldName) =>
                            addNewRow(fieldName, setFieldsConfig)
                        }
                        deleteRow={(fieldName, mapIndex) =>
                            deleteRow(fieldName, mapIndex, setFieldsConfig)
                        }
                    />
                )}

                {/* Ignore Characters section */}
                {fieldsConfig[selectedField]?.showIgnoreCharacters && (
                    <IgnoreCharactersSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleIgnoreCharacterChange={(fieldName, charIndex, newValue) =>
                            handleIgnoreCharacterChange(fieldName, charIndex, newValue, setFieldsConfig)
                        }
                        addIgnoreCharacter={(fieldName) =>
                            addIgnoreCharacter(fieldName, setFieldsConfig)
                        }
                        removeIgnoreCharacter={(fieldName, charIndex) =>
                            removeIgnoreCharacter(fieldName, charIndex, setFieldsConfig)
                        }
                    />
                )}

                {/* Ignore Rows section */}
                {fieldsConfig[selectedField]?.showIgnoreRows && (
                    <IgnoreRowsSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleIgnoreRowChange={(fieldName, rowIndex, newValue) =>
                            handleIgnoreRowChange(fieldName, rowIndex, newValue, setFieldsConfig)
                        }
                        addIgnoreRow={(fieldName) => addIgnoreRow(fieldName, setFieldsConfig)}
                        removeIgnoreRow={(fieldName, rowIndex) => removeIgnoreRow(fieldName, rowIndex, setFieldsConfig)}
                    />
                )}


                {/* Number Thresholds section */}
                {fieldsConfig[selectedField]?.showNumberThresholds && (
                    <NumberThresholdsSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleNumberTransformationChange={(fieldName, type, value) =>
                            handleNumberTransformationChange(fieldName, type, value, setFieldsConfig)
                        }
                    />
                )}

                {/* Rounding section */}
                {fieldsConfig[selectedField]?.showRounding && (
                    <RoundingSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleNumberTransformationChange={handleNumberTransformationChange}
                        setFieldsConfig={setFieldsConfig}
                    />
                )}

                {fieldsConfig[selectedField]?.showScaling && (
                    <ScalingSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleNumberTransformationChange={handleNumberTransformationChange}
                        setFieldsConfig={setFieldsConfig}
                    />
                )}

                {fieldsConfig[selectedField]?.showOffsetAdjustment && (
                    <OffsetAdjustmentSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleNumberTransformationChange={handleNumberTransformationChange}
                        setFieldsConfig={setFieldsConfig}
                    />
                )}

                {/* Aggregation section */}
                {fieldsConfig[selectedField]?.showAggregation && (
                    <AggregationMethodSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleAggregationChange={(fieldName, type, value) =>
                            handleAggregationChange(fieldName, type, value, setFieldsConfig)
                        }
                    />
                )}

                {/* Case Conversion section */}
                {fieldsConfig[selectedField]?.showCaseConversion && (
                    <UppercaseLowercaseSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleTextTransformationChange={(fieldName, type, value) =>
                            handleTextTransformationChange(fieldName, type, value, setFieldsConfig)
                        }
                    />
                )}

                {/* Substring Extraction section */}
                {fieldsConfig[selectedField]?.showSubstringExtraction && (
    <SubstringExtractionSection
        fieldName={selectedField}
        fieldsConfig={fieldsConfig}
        handleTextTransformationChange={(fieldName, type, value) =>
            handleTextTransformationChange(fieldName, type, value, setFieldsConfig)
        }
    />
)}


                {/* Date Thresholds section */}
                {fieldsConfig[selectedField]?.showDateThresholds && (
                    <DateThresholdsSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleDateTransformationChange={handleDateTransformationChange}
                        setFieldsConfig={setFieldsConfig}
                    />
                )}

                {/* Format Conversion section */}
                {fieldsConfig[selectedField]?.showFormatConversion && (
                    <FormatConversionSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleDateTransformationChange={(fieldName, type, value) =>
                            handleDateTransformationChange(fieldName, type, value, setFieldsConfig)
                        }
                    />
                )}

                {/* Date Arithmetic section */}
                {fieldsConfig[selectedField]?.showDateArithmetic && (
                    <DateArithmeticSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleDateTransformationChange={(fieldName, type, value) =>
                            handleDateTransformationChange(fieldName, type, value, setFieldsConfig)
                        }
                    />
                )}

                {/* Extract Date Component section */}
                {fieldsConfig[selectedField]?.showExtractDateComponent && (
                    <ExtractDateComponentSection
                        fieldName={selectedField}
                        fieldsConfig={fieldsConfig}
                        handleDateTransformationChange={(fieldName, type, value) =>
                            handleDateTransformationChange(fieldName, type, value, setFieldsConfig)
                        }
                    />
                )}

{/* Conditionally render Save Configurations button */}
{(fieldsConfig[selectedField]?.showMappings || 
  fieldsConfig[selectedField]?.showIgnoreCharacters ||
  fieldsConfig[selectedField]?.showNumberThresholds || 
  fieldsConfig[selectedField]?.showDateThresholds ||
  fieldsConfig[selectedField]?.showRounding || 
  fieldsConfig[selectedField]?.showCaseConversion ||
  fieldsConfig[selectedField]?.showSubstringExtraction || 
  fieldsConfig[selectedField]?.showScaling ||
  fieldsConfig[selectedField]?.showOffsetAdjustment || 
  fieldsConfig[selectedField]?.showFormatConversion ||
  fieldsConfig[selectedField]?.showDateArithmetic || 
  fieldsConfig[selectedField]?.showExtractDateComponent ||
  fieldsConfig[selectedField]?.showAggregation || 
  fieldsConfig[selectedField]?.showIgnoreRows || // Added showIgnoreRows
  fieldsConfig[selectedField]?.showUnifiedMappings ||
  fieldsConfig[selectedField]?.showDelimiter) && ( // Added showDelimiter
    <button onClick={() => saveMappings(selectedField)}>Save Configurations</button>
)}


            </div>
        </div>
    )}
</div>


                
                  
                </div>
            )}


        </div>
    );
}


export default TransformationsTab;
